import { StepType } from "@redotech/redo-flows-core-common/common";
import { AssignV3Strategy } from "@redotech/redo-model/advanced-flow/rules/actions";
import {
  ErrorHandlingStrategy,
  QueryOperator,
  RuleActionType,
  RuleConditionType,
  RuleTriggerType,
} from "@redotech/redo-model/advanced-flow/rules/types";
import { SchemaType } from "@redotech/redo-model/advanced-flow/schemas/schemas";
import { DefaultRule } from "../types";

/**
 * Default rule: Unassign on conversation reopened
 *
 * When a conversation transitions to OPEN from a terminal/non-working
 * status, clear any existing assignee so it re-enters the routing queue.
 * Typical path: a customer replies to a closed ticket, `reopen-on-customer-
 * reply` flips the status to OPEN, which fires `CONVERSATION_STATUS_CHANGED`
 * and this rule clears the assignee.
 *
 * IN_PROGRESS is excluded from the prior-status check: when an agent is
 * actively working on a ticket and the customer replies, the ticket moves
 * back to OPEN but the assignee is retained — the agent should keep
 * ownership so the reply doesn't get silently re-queued.
 *
 * Enabled by default, no configuration — merchants toggle via the
 * rules list.
 */
export const unassignOnConversationReopenedRule = {
  id: "default-unassign-on-conversation-reopened",
  name: "Unassign on conversation reopened",
  description:
    "When a conversation is reopened, clear its assignee so it re-enters the routing queue.",
  hidden: false,
  triggerType: RuleTriggerType.CONVERSATION_STATUS_CHANGED,

  defaultConfig: {
    defaultRuleId: "default-unassign-on-conversation-reopened",
    enabled: true,
    sortIndex: 5,
    configurations: {},
  },

  template: {
    name: "Unassign on conversation reopened",
    description:
      "When a conversation is reopened, clear its assignee so it re-enters the routing queue.",
    enabled: true,
    schemaType: SchemaType.RULES_V3,
    steps: [
      {
        type: StepType.TRIGGER,
        id: "trigger",
        schemaType: SchemaType.RULES_V3,
        category: "Rules",
        key: RuleTriggerType.CONVERSATION_STATUS_CHANGED,
        nextId: "conditions",
      },
      {
        type: StepType.RULE_CONDITIONS_V3,
        id: "conditions",
        conditionGroups: {
          operator: "AND",
          conditions: [
            {
              conditionType: RuleConditionType.TICKET_STATUS,
              statuses: ["OPEN"],
              queryOperator: QueryOperator.IS,
            },
            {
              conditionType: RuleConditionType.PREVIOUS_TICKET_STATUS,
              statuses: ["IN_PROGRESS"],
              queryOperator: QueryOperator.IS_NOT,
            },
          ],
        },
        nextTrueId: "actions",
      },
      {
        type: StepType.RULE_ACTIONS_V3,
        id: "actions",
        actions: [
          {
            actionType: RuleActionType.ASSIGN_V3,
            mode: { _tag: "unassign" },
            strategy: AssignV3Strategy.ROUND_ROBIN,
            maxTicketsPerUser: null,
          },
        ],
        errorHandlingStrategy: ErrorHandlingStrategy.FAIL_FAST,
      },
    ],
  },
} as const satisfies DefaultRule;
